var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, forwardRef, useState, } from 'react';
import classNames from 'classnames';
import { useApplicationSize } from '@anwb/providers-application';
import { PanelContent } from './components/PanelContent';
import { PanelFooter } from './components/PanelFooter';
import { PanelHeader } from './components/PanelHeader';
import { PanelLinks } from './components/PanelLinks';
import { PanelMedia } from './components/PanelMedia';
import { PanelSticker } from './components/PanelSticker';
import { PanelContext } from './context';
import { ELEMENT_TYPES } from './helpers/ElementTypes';
import { getChildPanelElementType } from './helpers/getChildPanelElementType';
import { PanelAnchor, PanelContainer, PanelInner, PanelMain, PanelPromotionContainer, PanelSpinner, PanelWrapper, } from './styles';
/**
 * The Panel component is a compound component that exposes all the building blocks of a panel.
 * The component itself is a wrapper that provides the correct styling and context.
 */
// eslint-disable-next-line prefer-arrow-callback
export const Panel = forwardRef(function PanelNewWithRef(_a, ref) {
    var { className: classNameProp, children, dataTest = 'panel', fullyClickable = false, hasOuterSpacing = true, href, isPromotional = false, isStacked, loading = false, lowPriority = false, selected, variant = 'informative' } = _a, props = __rest(_a, ["className", "children", "dataTest", "fullyClickable", "hasOuterSpacing", "href", "isPromotional", "isStacked", "loading", "lowPriority", "selected", "variant"]);
    const applicationSize = useApplicationSize();
    const classes = classNames('panel__container', classNameProp);
    const [hasMedia, setHasMedia] = useState(false);
    /**
     * The children must be split up, this is needed because the element where the children are placed in has a display
     * flex. This causes issues with the sticker. Therefore all children except the PanelFooter are wrapped in a
     * PanelMain.
     */
    const childs = Children.toArray(children);
    const mainChilds = childs.filter((child) => getChildPanelElementType(child) !== ELEMENT_TYPES.FOOTER);
    const footer = childs
        .filter((child) => getChildPanelElementType(child) === ELEMENT_TYPES.FOOTER)
        .shift();
    const wrapWithAnchor = (childrenParam) => {
        if (fullyClickable) {
            return (React.createElement(PanelAnchor, { href: href, "data-test": `${dataTest}-link-wrapper` }, childrenParam));
        }
        return childrenParam;
    };
    const wrapWithOuterSpacing = (childrenParam) => {
        if (hasOuterSpacing) {
            return React.createElement(PanelWrapper, null, childrenParam);
        }
        return childrenParam;
    };
    /**
     * Do we want this logic:
     *  - kept in here?
     *  - moved to the implementing side, telling consumers to apply this container themselves?
     */
    const wrapChildrenWithPromotion = (childrenParam) => {
        if (isPromotional) {
            return React.createElement(PanelPromotionContainer, null, childrenParam);
        }
        return childrenParam;
    };
    return (React.createElement(PanelContext.Provider, { value: {
            dataTest,
            fullyClickable,
            hideContent: lowPriority && applicationSize === 'small',
            href,
            setHasMedia,
        } }, wrapWithOuterSpacing(wrapWithAnchor(React.createElement(PanelContainer, Object.assign({ className: classes, "data-test": dataTest, ref: ref, variant: variant, "$hasMedia": hasMedia, "$isLoading": loading }, props),
        wrapChildrenWithPromotion(React.createElement(PanelInner, null,
            mainChilds.length > 0 && React.createElement(PanelMain, null, mainChilds),
            footer)),
        loading && React.createElement(PanelSpinner, { message: "Een ogenblik geduld, alstublieft" }))))));
});
Panel.Footer = PanelFooter;
Panel.Header = PanelHeader;
Panel.Media = PanelMedia;
Panel.Content = PanelContent;
Panel.Links = PanelLinks;
Panel.Sticker = PanelSticker;
export default Panel;
//# sourceMappingURL=Panel.js.map